import GridDuck from 'gridduck';
import cookie from 'react-cookies';
import {GATEWAY_COMMAND_SOCKET_PATH, RPC_METHODS, isMethodAllowedForModel, isRpcMethodName, modelFamilyOf} from '@gridduck/gateway-rpc';
import type {
	BrowserRpcParams,
	GatewayCapabilities,
	GatewayCommandClientErrorCode,
	GatewayCommandClientMessage,
	GatewayCommandServerMessage,
	OneShotMethodName,
	RpcMethodName,
	RpcChunk,
	RpcResult,
	StreamMethodName,
} from '@gridduck/gateway-rpc';

// Client for the gd-client-api command socket: invoke() is one command per socket, stream() keeps one open until stopped.

// Method names pages check capability lists against; a typo is a build error here rather than a silent miss there.
export const LOG_STREAM_METHOD: StreamMethodName = 'gateway.logStream';
export const ZIGBEE_STATE_METHOD: OneShotMethodName = 'zigbee.getState';

export interface GatewayCommandError extends Error {
	code?: GatewayCommandClientErrorCode;
}

export interface StreamHandlers<M extends StreamMethodName> {
	onChunk?: (chunk: RpcChunk<M>) => void;
	onEnd?: (result: RpcResult<M>) => void;
	onError?: (err: GatewayCommandError) => void;
}

// Params are optional exactly when the method needs none; the timeout defaults to the contract's budget.
type InvokeArgs<M extends OneShotMethodName> = {} extends BrowserRpcParams<M>
	? [BrowserRpcParams<M>?, number?]
	: [BrowserRpcParams<M>, number?];

// The SDK's typings omit its `config` getter, so read the two members used through a structural view.
interface SdkView {
	config?: {baseUrl?: string};
	getAccessToken?: () => unknown;
}
const sdk: SdkView = GridDuck;

const AUTH_TIMEOUT_MS = 10000;

let requestCounter = 0;

function commandError(message: string, code?: GatewayCommandClientErrorCode): GatewayCommandError {
	const err: GatewayCommandError = new Error(message);
	if (code) err.code = code;
	return err;
}

// JS pages can pass any string, so an unknown name gets no default and the server picks.
function defaultTimeout(method: string): number | undefined {
	return isRpcMethodName(method) ? RPC_METHODS[method].timeoutMs : undefined;
}

// `https://v2.api.gridduck.com/` -> `wss://v2.api.gridduck.com/gateway-command`
function socketUrl(): string {
	const base = (sdk.config && sdk.config.baseUrl) || 'https://v2.api.gridduck.com/';
	return base.replace(/^http/, 'ws').replace(/\/+$/, '') + GATEWAY_COMMAND_SOCKET_PATH;
}

// The SDK's AccessToken keeps the string on `.token`, and some call sites wrap it again as `{accessToken}`.
function readToken(held: unknown): string | null {
	if (!held) return null;
	if (typeof held === 'string') return held;
	if (typeof held !== 'object') return null;
	const holder = held as {token?: unknown; accessToken?: unknown};
	if (typeof holder.token === 'string') return holder.token;
	if (holder.accessToken) return readToken(holder.accessToken);
	return null;
}

// The in-memory SDK first; the cookie the dashboard restores from on boot as the fallback.
function bearerToken(): string | null {
	const fromSdk = readToken(sdk.getAccessToken && sdk.getAccessToken());
	if (fromSdk) return fromSdk;
	return readToken(cookie.load('accessToken'));
}

function send(ws: WebSocket, message: GatewayCommandClientMessage): void {
	ws.send(JSON.stringify(message));
}

function parse(evt: MessageEvent): GatewayCommandServerMessage | null {
	try {
		return JSON.parse(evt.data);
	} catch (e) {
		return null;
	}
}

// The token rides the handshake as the subprotocol after `bearer`, the only handshake header a browser can set.
function connect(): Promise<WebSocket> {
	return new Promise(function (resolve, reject) {
		const token = bearerToken();
		if (!token) return reject(new Error('Not signed in'));

		let settled = false;
		const ws = new WebSocket(socketUrl(), ['bearer', token]);

		const timer = setTimeout(function () {
			if (settled) return;
			settled = true;
			try { ws.close(); } catch (e) { /* already closing */ }
			reject(new Error('Timed out connecting to the command service'));
		}, AUTH_TIMEOUT_MS);

		ws.onopen = function () {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			// Handed over to the caller, which installs its own onmessage.
			resolve(ws);
		};

		// A refused upgrade looks like an unreachable one: the 401 body is not exposed to script.
		ws.onerror = function () {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			reject(new Error('Could not authenticate with the command service'));
		};

		ws.onclose = function () {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			reject(new Error('The command service closed the connection'));
		};
	});
}

// Rejects with the device's own message and a closed-set `code`, so a caller can surface it directly.
export async function invoke<M extends OneShotMethodName>(
	gatewayId: string,
	method: M,
	...args: InvokeArgs<M>
): Promise<RpcResult<M>> {
	const [params, timeout] = args;
	const ws = await connect();
	const requestId = 'r' + (++requestCounter);

	return new Promise(function (resolve, reject) {
		let settled = false;

		const finish = function (settle: () => void) {
			if (settled) return;
			settled = true;
			try { ws.close(); } catch (e) { /* already closing */ }
			settle();
		};

		ws.onmessage = function (evt) {
			const msg = parse(evt);
			if (!msg) return;
			if (msg.requestId && msg.requestId !== requestId) return;

			if (msg.type === 'result') return finish(() => resolve(msg.result as RpcResult<M>));
			if (msg.type === 'error') {
				return finish(() => reject(commandError(msg.error || 'The command failed', msg.code)));
			}
		};

		// A socket that drops before its answer is a failure, not a silent success.
		ws.onclose = function () {
			if (settled) return;
			settled = true;
			reject(commandError('The connection closed before the gateway answered', 'DISCONNECTED'));
		};

		ws.onerror = function () {
			if (settled) return;
			settled = true;
			reject(new Error('The connection to the command service failed'));
		};

		send(ws, {
			action: 'invoke',
			requestId,
			gatewayId,
			method,
			params,
			timeout: timeout !== undefined ? timeout : defaultTimeout(method),
		});
	});
}

// Answered from the server's cache of the hub's hello, so it costs no radio time and is cheap on page load.
export async function capabilities(gatewayId: string): Promise<GatewayCapabilities> {
	const ws = await connect();
	const requestId = 'r' + (++requestCounter);

	return new Promise(function (resolve, reject) {
		let settled = false;

		const finish = function (settle: () => void) {
			if (settled) return;
			settled = true;
			try { ws.close(); } catch (e) { /* already closing */ }
			settle();
		};

		ws.onmessage = function (evt) {
			const msg = parse(evt);
			if (!msg) return;
			if (msg.requestId && msg.requestId !== requestId) return;

			if (msg.type === 'result') {
				return finish(() => resolve((msg.result as GatewayCapabilities) || {methods: []}));
			}
			if (msg.type === 'error') {
				return finish(() => reject(commandError(msg.error || 'Could not read capabilities', msg.code)));
			}
		};

		ws.onclose = function () {
			if (settled) return;
			settled = true;
			reject(new Error('The connection closed before the capabilities arrived'));
		};

		ws.onerror = function () {
			if (settled) return;
			settled = true;
			reject(new Error('The connection to the command service failed'));
		};

		send(ws, {action: 'capabilities', requestId, gatewayId});
	});
}

// Keeps the socket open and relays chunks until the caller calls stop() or the stream ends.
export async function stream<M extends StreamMethodName>(
	gatewayId: string,
	method: M,
	params?: BrowserRpcParams<M>,
	handlers?: StreamHandlers<M>,
): Promise<{stop: () => void}> {
	const ws = await connect();
	const requestId = 'r' + (++requestCounter);
	const on: StreamHandlers<M> = handlers || {};
	let stopped = false;
	let ended = false;

	ws.onmessage = function (evt) {
		const msg = parse(evt);
		if (!msg) return;
		if (msg.requestId && msg.requestId !== requestId) return;

		if (msg.type === 'chunk' && on.onChunk) return on.onChunk(msg.data as RpcChunk<M>);

		if (msg.type === 'result') {
			ended = true;
			if (on.onEnd) on.onEnd(msg.result as RpcResult<M>);
			try { ws.close(); } catch (e) { /* already closing */ }
			return;
		}

		if (msg.type === 'error') {
			ended = true;
			// A stop() the caller asked for arrives as CANCELLED, which is not a failure.
			if (!stopped && on.onError) on.onError(commandError(msg.error || 'The stream failed', msg.code));
			try { ws.close(); } catch (e) { /* already closing */ }
		}
	};

	ws.onerror = function () {
		if (!stopped && on.onError) on.onError(new Error('The connection to the command service failed'));
	};

	// connect()'s onclose is inert once open, so without this a stream dropping mid-tail would be silent.
	ws.onclose = function () {
		if (stopped || ended) return;
		ended = true;
		if (on.onError) on.onError(commandError('The connection closed while streaming', 'DISCONNECTED'));
	};

	send(ws, {action: 'invoke', requestId, gatewayId, method, params, timeout: defaultTimeout(method)});

	return {
		stop: function () {
			stopped = true;
			// Cancel first, so the device stops producing rather than tailing on to a closing socket.
			try {
				send(ws, {action: 'cancel', requestId});
			} catch (e) { /* socket already gone; the device deadline still ends it */ }
			try { ws.close(); } catch (e) { /* already closing */ }
		},
	};
}

type GatewayModel = {modelNumber?: string} | null | undefined;

// Which firmware family a gateway runs, from the contract's model table rather than a model number spelled out here.
export function isPiDuck(gateway: GatewayModel): boolean {
	return modelFamilyOf(gateway && gateway.modelNumber) === 'pi-duck';
}

// Whether this gateway's model serves one method; the same table gd-client-api refuses a command by.
export function gatewaySupportsMethod(gateway: GatewayModel, method: RpcMethodName): boolean {
	return isMethodAllowedForModel(gateway && gateway.modelNumber, method);
}

const gatewayCommandSocket = {invoke, stream, capabilities, isPiDuck, gatewaySupportsMethod};
export default gatewayCommandSocket;
